import { useState, useEffect, useCallback, useRef } from "react";
import API from "../api";

export const useNews = () => {
  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const fetchingRef = useRef(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const fetchNews = useCallback(async (pageNum = 1) => {
    if (!isOnline) {
      setNewsError("You appear to be offline. Please check your connection.");
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoadingNews(true);
    setNewsError(null);
    try {
      const response = await API.get("/news/check", { params: { page: pageNum } });
      const impactedTrades = response.data?.impacted_trades || [];
      const articles = impactedTrades.flatMap(trade => (trade.news || []).map(article => ({
        symbol: trade.symbol,
        asset_type: trade.asset_type,
        headline: article.title,
        url: article.url,
        source: article.source,
        publishedAt: article.publishedAt,
        urlToImage: article.urlToImage,
      })));
      setNews(current => pageNum === 1 ? articles : [...current, ...articles]);
      setPage(pageNum);
      setHasMore(articles.length > 0);
    } catch (error) {
      setNewsError(error.response?.data?.detail || "Could not load market news.");
    } finally {
      fetchingRef.current = false;
      setLoadingNews(false);
    }
  }, [isOnline]);

  useEffect(() => { if (isOnline) fetchNews(1); }, [fetchNews, isOnline]);

  const loadMoreGeneralNews = useCallback(() => fetchNews(page + 1), [fetchNews, page]);

  return { news, loadingNews, newsError, isOnline, page, hasMore, fetchNews, loadMoreGeneralNews };
};
